import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ReviewsQueryKeys } from '@app/model/review/service/ReviewsQueryKeys';
import { ReviewService } from '@app/model/review/service/ReviewService';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';
import Message from '@app/component/message/Message';
import ReviewsStateStatisticsFragment
	from '@app/site/secured/review-manager/fragment/property-overview/component/ReviewsStateStatisticsFragment';
import Utilities from '@util/Utilities';
import ReviewsPropertyInsightsCategoryPerformance
	from '@app/site/secured/review-manager/fragment/property-overview/_insights/ReviewsPropertyInsightsCategoryPerformance';
import ReviewsPropertyInsightsKeyObservations
	from '@app/site/secured/review-manager/fragment/property-overview/_insights/ReviewsPropertyInsightsKeyObservations';
import ReviewsPropertyInsightsCompetitiveAnalysis
	from '@app/site/secured/review-manager/fragment/property-overview/_insights/ReviewsPropertyInsightsCompetitiveAnalysis';
import ReviewsCompetitorInsightsKeyObservations
	from '@app/site/secured/review-manager/fragment/property-overview/_insights/ReviewsCompetitorInsightsKeyObservations';

const ReviewsPropertyInsightsMonthlyDigest = React.memo(() => {

	const { companyHotel } = useSelectedCompanyHotel();

	const { data: monthlyDigest, isSuccess, isLoading, isError } = useQuery({
		queryKey: ReviewsQueryKeys.monthlyDigest(companyHotel.id),
		queryFn: () => ReviewService.findReviewsMonthlyDigest(companyHotel.id),
	});

	if (isLoading)
		return <TablePlaceholder />;

	if (isError)
		return <Message state="error">The monthly digest could not be loaded</Message>;

	if (isSuccess) {
		const current = monthlyDigest?.current;
		const previous = monthlyDigest?.previous;
		const currentMainHotelState = current?.findMainHotelState();
		if (!!monthlyDigest && !!current && !!currentMainHotelState) {
			const currentPeriod = current.from != null ? Utilities.formatDateString(current.from, 'MMM YYYY') : '';
			const previousPeriod = previous?.from != null ? Utilities.formatDateString(previous.from, 'MMM YYYY') : '';
			const previousMainHotelState = previous?.findMainHotelState();

			return <>
				<ReviewsStateStatisticsFragment
					statistics={currentMainHotelState.statistics}
					rank={current.rank}
					previousStatistics={previousMainHotelState?.statistics}
					variant="insightsDigest"
				/>
				<div className="flex items-center mb-4">
					<div className="relative flex shrink-0 justify-start">
						<span className="bg-gray-100 text-sm font-normal text-gray-500 pr-3">
							{currentPeriod}
							{previousPeriod !== '' && (
								<span
									className="ml-2">vs. {previousPeriod}</span>
							)}
						</span>
					</div>
					<div className="w-full border-t border-gray-300"></div>
				</div>
				<div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
					<ReviewsPropertyInsightsCategoryPerformance digest={monthlyDigest} />
					<ReviewsPropertyInsightsKeyObservations digest={monthlyDigest} />
					<ReviewsPropertyInsightsCompetitiveAnalysis digest={monthlyDigest} />
					<ReviewsCompetitorInsightsKeyObservations digest={monthlyDigest} />
				</div>
			</>;
		} else {
			return <Message state="error">The monthly digest could not be found</Message>;
		}
	}

	return <div>Monthly Digest</div>;
});

export default ReviewsPropertyInsightsMonthlyDigest;
